import { MercatorCoordinate } from 'maplibre-gl';
import { CAMERA_STATES } from './camera.js';

export const INTRO_MIN_ZOOM = 13.71;
export const INTRO_DURATION_MS = 9800;

/** Behold løftet fra holdeplassen, øk så tempoet gjennom oversikten og buen. */
export function introProgressAtTime(elapsed) {
  const ms = Math.max(0, Math.min(INTRO_DURATION_MS, elapsed));
  const acceleration = Math.max(0, (ms - 2200) / 7600);
  return (ms + 4200 * acceleration ** 2) / 14000;
}
export const INTRO_APEX_PROGRESS = 0.5;
export const INTRO_SOUTH_PROGRESS = 0.7;
export const INTRO_SHOTS = {
  start: { center: [10.50648, 59.94286], zoom: 22.53, pitch: 80, bearing: -45.7 },
  apex: {
    center: [10.66158610851005, 59.91782862093595],
    zoom: 13.717636398419073,
    pitch: 58.49999999999999,
    bearing: -44.852012431169044,
    roll: 0,
    elevation: 5.778653774714045,
    padding: { top: 0, bottom: 0, left: 0, right: 0 },
  },
  clocktower: {
    center: [...CAMERA_STATES.akerBrygge.center],
    zoom: CAMERA_STATES.akerBrygge.zoom,
    pitch: CAMERA_STATES.akerBrygge.endPitch,
    bearing: CAMERA_STATES.akerBrygge.bearing,
  },
};

const clamp = (t) => Math.max(0, Math.min(1, t));
const smooth = (t) => { t = clamp(t); return t * t * t * (10 + t * (-15 + 6 * t)); };
const mix = (a, b, t) => a + (b - a) * t;

// Hermite-tangenter angis per enhet av HELE tidslinjen. Det gir samme
// hastighet inn og ut av toppunktet, selv om delene har ulik varighet.
function segment(t, from, to, a, b, va, vb) {
  const d = to - from;
  const u = clamp((t - from) / d);
  return a.map((value, i) =>
    (2 * u ** 3 - 3 * u ** 2 + 1) * value
    + (u ** 3 - 2 * u ** 2 + u) * d * va[i]
    + (-2 * u ** 3 + 3 * u ** 2) * b[i]
    + (u ** 3 - u ** 2) * d * vb[i]);
}

const radians = (degrees) => degrees * Math.PI / 180;
const DEFAULT_VIEWPORT = { height: 1000, fov: 36.86989764584402 };

function cameraDistance(zoom, viewport) {
  return viewport.height / (2 * Math.tan(radians(viewport.fov) / 2)) / (512 * 2 ** zoom);
}

function cameraOffset(distance, pitch, bearing) {
  const horizontal = distance * Math.sin(radians(pitch));
  return [-horizontal * Math.sin(radians(bearing)), horizontal * Math.cos(radians(bearing))];
}

/** Faktisk kameraposisjon i Mercator-rom, ikke kartets siktepunkt. */
export function introCameraPosition(camera, viewport = DEFAULT_VIEWPORT) {
  const center = MercatorCoordinate.fromLngLat(camera.center, camera.elevation ?? 0);
  const distance = cameraDistance(camera.zoom, viewport);
  const offset = cameraOffset(distance, camera.pitch, camera.bearing);
  return new MercatorCoordinate(center.x + offset[0], center.y + offset[1],
    center.z + distance * Math.cos(radians(camera.pitch)));
}

/** Én kamerabane: løft, toppunkt, sørover i høyden, så nordover til landing. */
export function sampleIntroCamera(progress, elevationAt = () => 0, viewport = DEFAULT_VIEWPORT) {
  const t = clamp(progress);
  const start = INTRO_SHOTS.start;
  const end = INTRO_SHOTS.clocktower;
  const apex = INTRO_SHOTS.apex;
  const peak = INTRO_APEX_PROGRESS;
  const turn = INTRO_SOUTH_PROGRESS;
  if (t === 0) return { ...start, center: [...start.center] };
  if (t === 1) return { ...end, center: [...end.center] };
  if (t === peak) return { ...apex, center: [...apex.center], padding: { ...apex.padding } };

  const tangent = [0.22, -0.034];
  const bearing = t < peak ? mix(start.bearing, apex.bearing, smooth(t / peak))
    : mix(apex.bearing, end.bearing, smooth((t - peak) / (1 - peak)));
  let center, zoom, pitch;
  if (t < peak) {
    center = t < 0.15 ? [...start.center]
      : segment(t, 0.15, peak, start.center, apex.center, [0, 0], tangent);
    zoom = t < 0.26 ? mix(start.zoom, 14.1, smooth(t / 0.26))
      : mix(14.1, apex.zoom, smooth((t - 0.26) / (peak - 0.26)));
    pitch = t < 0.15 ? mix(start.pitch, 20, smooth(t / 0.15))
      : mix(20, apex.pitch, smooth((t - 0.15) / 0.13));
  } else {
    const from = introCameraPosition(apex, viewport);
    const to = introCameraPosition(end, viewport);
    const fromLngLat = from.toLngLat();
    const toLngLat = to.toLngLat();
    const south = MercatorCoordinate.fromLngLat([
      Math.max(fromLngLat.lng + 0.025, toLngLat.lng + 0.01),
      Math.min(fromLngLat.lat - 0.012, toLngLat.lat - 0.025),
    ]);
    // Samme fysiske hastighet gjennom toppunktet som den godkjente oppturen.
    const velocity = [tangent[0] / 360,
      -tangent[1] / (360 * Math.cos(radians(apex.center[1])))];
    const cameraXY = t < turn
      ? segment(t, peak, turn, [from.x, from.y], [south.x, south.y], velocity, [0, 0])
      : [mix(south.x, to.x, smooth((t - turn) / (1 - turn))),
        mix(south.y, to.y, smooth((t - turn) / (1 - turn)))];

    // Interpoler avstand i 3D, ikke zoomtall: behold høyde ute over fjorden.
    // Vi synker gjennom sørsvingen selv når nord/sør-hastigheten skifter tegn.
    const d0 = cameraDistance(apex.zoom, viewport);
    const d1 = cameraDistance(end.zoom, viewport);
    const dSouth = mix(d0, d1, 0.2);
    const descentVelocity = -2 * (d0 - d1);
    const distance = t < turn
      ? segment(t, peak, turn, [d0], [dSouth], [0], [descentVelocity])[0]
      : segment(t, turn, 1, [dSouth], [d1], [descentVelocity], [0])[0];
    zoom = apex.zoom + Math.log2(d0 / distance);
    pitch = mix(apex.pitch, end.pitch, smooth((t - turn) / (1 - turn)));
    const offset = cameraOffset(distance, pitch, bearing);
    const target = new MercatorCoordinate(cameraXY[0] - offset[0], cameraXY[1] - offset[1]).toLngLat();
    center = [target.lng, target.lat];
  }

  // Ta med høyden fra det kopierte toppunktet uten høydehopp ved passering.
  const apexWeight = t < peak ? smooth((t - 0.15) / (peak - 0.15))
    : 1 - smooth((t - peak) / (0.94 - peak));
  const elevation = mix(elevationAt(center), apex.elevation, apexWeight);
  return { center, zoom, pitch, bearing, elevation, roll: apex.roll, padding: { ...apex.padding } };
}

export const INTRO2_END = {
  center: [10.667139348095247, 59.92769448765898],
  zoom: 13.32786142362675,
  pitch: 63.50000000000001,
  bearing: -38.59999999999985,
  roll: 0,
  elevation: 84.52998572256224,
  padding: { top: 0, bottom: 0, left: 0, right: 0 },
};

/** Én sammenhengende fysisk kamerabane uten etapper eller pitch-vendepunkt. */
export function sampleIntro2Camera(progress, elevationAt = () => 0, viewport = DEFAULT_VIEWPORT) {
  const t = clamp(progress / 0.45);
  const start = INTRO_SHOTS.start;
  const end = INTRO2_END;
  if (t === 0) return { ...start, center: [...start.center] };
  if (t === 1) return { ...end, center: [...end.center], padding: { ...end.padding } };
  const travel = smooth(t);
  // Rask høydegevinst, men én glatt kurve helt fra start til slutt.
  const lift = clamp(smooth(1 - (1 - t) ** 3));
  const from = introCameraPosition(start, viewport);
  const to = introCameraPosition(end, viewport);
  const distance = mix(cameraDistance(start.zoom, viewport), cameraDistance(end.zoom, viewport), lift);
  const pitch = mix(start.pitch, end.pitch, travel);
  const bearing = mix(start.bearing, end.bearing, travel);
  const offset = cameraOffset(distance, pitch, bearing);
  const target = new MercatorCoordinate(
    mix(from.x, to.x, travel) - offset[0],
    mix(from.y, to.y, travel) - offset[1],
  ).toLngLat();
  return {
    center: [target.lng, target.lat],
    zoom: Math.max(end.zoom, end.zoom + Math.log2(cameraDistance(end.zoom, viewport) / distance)),
    pitch,
    bearing,
    // Kalleren låser starthøyden; vi følger ikke DEM-detaljer underveis.
    elevation: mix(elevationAt(start.center), end.elevation, travel),
    roll: end.roll,
    padding: { ...end.padding },
  };
}

export const INTRO3_TILT_PROGRESS = 0.065;
export const INTRO3_END_PROGRESS = 0.55;
export const INTRO3_TILT = {
  center: [10.506518187637255, 59.94283782364229],
  zoom: 22.86688075911627,
  pitch: 60.499999999999964,
  bearing: -40.97499929066476,
  roll: 0,
  elevation: 191.5386584958275,
  padding: { top: 0, bottom: 0, left: 0, right: 0 },
};
export const INTRO3_END = {
  center: [10.684769745492986, 59.92412611143911],
  zoom: 12.635459034238977,
  pitch: INTRO3_TILT.pitch,
  bearing: INTRO3_TILT.bearing,
  roll: 0,
  elevation: 40.643507841283665,
  padding: { top: 0, bottom: 0, left: 0, right: 0 },
};

/** Vipp ved busstoppet, trekk så jevnt ut med fast kameraretning. */
function sampleTiltZoomCamera(progress, elevationAt, end) {
  const t = clamp(progress);
  const start = INTRO_SHOTS.start;
  const tilt = INTRO3_TILT;
  if (t === 0) return { ...start, center: [...start.center] };
  if (t === INTRO3_TILT_PROGRESS) return { ...tilt, center: [...tilt.center], padding: { ...tilt.padding } };
  if (t >= INTRO3_END_PROGRESS) return { ...end, center: [...end.center], padding: { ...end.padding } };
  if (t < INTRO3_TILT_PROGRESS) {
    const u = smooth(t / INTRO3_TILT_PROGRESS);
    return {
      center: start.center.map((value, i) => mix(value, tilt.center[i], u)),
      zoom: mix(start.zoom, tilt.zoom, u),
      pitch: mix(start.pitch, tilt.pitch, u),
      bearing: mix(start.bearing, tilt.bearing, u),
      elevation: mix(elevationAt(start.center), tilt.elevation, u),
      roll: tilt.roll,
      padding: { ...tilt.padding },
    };
  }
  const u = smooth((t - INTRO3_TILT_PROGRESS) / (INTRO3_END_PROGRESS - INTRO3_TILT_PROGRESS));
  const from = MercatorCoordinate.fromLngLat(tilt.center);
  const to = MercatorCoordinate.fromLngLat(end.center);
  const center = new MercatorCoordinate(mix(from.x, to.x, u), mix(from.y, to.y, u)).toLngLat();
  // Med fast vinkel gir samme interpolasjon av senter og kameraavstand
  // én rett fysisk utzoom-bane. Ingen ekstra etappe ved mellomutsnittet.
  const zoom = -Math.log2(mix(2 ** -tilt.zoom, 2 ** -end.zoom, u));
  return {
    center: [center.lng, center.lat],
    zoom: Math.max(end.zoom, zoom),
    pitch: tilt.pitch,
    bearing: tilt.bearing,
    elevation: mix(tilt.elevation, end.elevation, u),
    roll: tilt.roll,
    padding: { ...tilt.padding },
  };
}


export const INTRO4_END = {
  center: [10.632057165823113, 59.92343914945877],
  zoom: 12.789477479827145,
  pitch: INTRO3_TILT.pitch,
  bearing: INTRO3_TILT.bearing,
  roll: 0,
  elevation: 107.22041690118834,
  padding: { top: 0, bottom: 0, left: 0, right: 0 },
};

export function sampleIntro3Camera(progress, elevationAt = () => 0) {
  return sampleTiltZoomCamera(progress, elevationAt, INTRO3_END);
}

export function sampleIntro4Camera(progress, elevationAt = () => 0) {
  return sampleTiltZoomCamera(progress, elevationAt, INTRO4_END);
}

// --- Intro 5: tre-punkts gjennomflyging (Bærum → midt → Aker Brygge) -----
// Alle tre deler bearing/pitch, så retning og vinkel er konstant og bare
// senter, zoom og høyde interpoleres. Første halvdel start→midt, andre
// halvdel midt→slutt, hver med myk ease.
export const INTRO5_START = {
  center: [10.54561419856583, 59.93337512201717],
  zoom: 12.7195883002307,
  pitch: 43.500000000000014,
  bearing: -47.474281146698786,
  roll: 0,
  elevation: 321.1853195672624,
  padding: { top: 0, bottom: 0, left: 0, right: 0 },
};
export const INTRO5_MID = {
  center: [10.718863629364705, 59.914310381762334],
  zoom: 12.657496718306302,
  pitch: 43.500000000000014,
  bearing: -47.474281146698786,
  roll: 0,
  elevation: 39.05837436022688,
  padding: { top: 0, bottom: 0, left: 0, right: 0 },
};
export const INTRO5_END = {
  center: [10.72832313217998, 59.90983030682008],
  zoom: 19.60051605096849,
  pitch: 43.500000000000014,
  bearing: -47.474281146698786,
  roll: 0,
  elevation: 1.926795619838401,
  padding: { top: 0, bottom: 0, left: 0, right: 0 },
};

const INTRO5_MID_PROGRESS = 0.4;

// Mobil-waypoints for intro 5. Portrett-viewport er høyt og smalt, så
// utsnittet blir et annet enn på desktop. Kalibrert med kamera-HUD-en
// (?minimal) i mobil-format – juster disse tre, ikke samplerlogikken.
export const INTRO5_MOBILE_START = {
  center: [10.538934439801722, 59.93798487301095],
  zoom: 14.168290523841007,
  pitch: 51,
  bearing: -70.58311661986193,
  roll: 0,
  elevation: 489.4572802738061,
  padding: { top: 0, bottom: 0, left: 0, right: 0 },
};
export const INTRO5_MOBILE_MID = {
  center: [10.656608408379583, 59.91877176969848],
  zoom: 11.49118927215901,
  pitch: 51,
  bearing: -70.58311661986193,
  roll: 0,
  elevation: 57.45248319225059,
  padding: { top: 0, bottom: 0, left: 0, right: 0 },
};
export const INTRO5_MOBILE_END = {
  center: [10.728006952319618, 59.909829151889824],
  zoom: 17.972977051599308,
  pitch: 51,
  bearing: -70.58311661986193,
  roll: 0,
  elevation: 3.207500795965694,
  padding: { top: 0, bottom: 0, left: 0, right: 0 },
};

function mixIntro5(a, b, u) {
  return {
    center: [mix(a.center[0], b.center[0], u), mix(a.center[1], b.center[1], u)],
    // Interpoler kameraavstand (2^-zoom), ikke zoomtall, så innzoomen føles jevn.
    zoom: -Math.log2(mix(2 ** -a.zoom, 2 ** -b.zoom, u)),
    pitch: mix(a.pitch, b.pitch, u),
    bearing: mix(a.bearing, b.bearing, u),
    elevation: mix(a.elevation, b.elevation, u),
    roll: 0,
    padding: { top: 0, bottom: 0, left: 0, right: 0 },
  };
}

/**
 * To segmenter, waypoint-drevet:
 *   1. start → midt: vanlig bevegelse ned mot korridoren.
 *   2. midt → slutt: zoom-dominert. Senter flytter seg bare det lille som
 *      skal til for å treffe Aker Brygge, mens zoom gjør jobben – så det
 *      leses som «skrått nedover mot målet», ikke en nordgående bue.
 * Samme logikk for desktop og mobil; bare waypoint-settet skiller dem.
 */
function sampleIntro5(progress, start, midWp, end) {
  const t = clamp(progress);
  if (t <= 0) return { ...start, center: [...start.center], padding: { ...start.padding } };
  if (t >= 1) return { ...end, center: [...end.center], padding: { ...end.padding } };
  if (t < INTRO5_MID_PROGRESS) {
    return mixIntro5(start, midWp, smooth(t / INTRO5_MID_PROGRESS));
  }
  return mixIntro5(midWp, end, smooth((t - INTRO5_MID_PROGRESS) / (1 - INTRO5_MID_PROGRESS)));
}

export function sampleIntro5Camera(progress) {
  return sampleIntro5(progress, INTRO5_START, INTRO5_MID, INTRO5_END);
}

export function sampleIntro5CameraMobile(progress) {
  return sampleIntro5(progress, INTRO5_MOBILE_START, INTRO5_MOBILE_MID, INTRO5_MOBILE_END);
}


// --- Intro 6: kopi av intro 5, egne waypoints så vi kan justere fritt -----
export const INTRO6_START = {
  center: [10.508458346568318, 59.94135556817204],
  zoom: 14.295952287505694,
  pitch: 46.50000000000002,
  bearing: -76.68558217576117,
  roll: 0,
  elevation: 232.11231710912824,
  padding: { top: 0, bottom: 0, left: 0, right: 0 },
};
export const INTRO6_MID = {
  center: [10.661139623543733, 59.917330754145325],
  zoom: 11.57299128028978,
  pitch: 46.50000000000002,
  bearing: -76.68558217576117,
  roll: 0,
  elevation: 5.71661420509449,
  padding: { top: 0, bottom: 0, left: 0, right: 0 },
};
export const INTRO6_END = {
  center: [10.728421929846832, 59.909787210848066],
  zoom: 16.95157789830261,
  pitch: 46.50000000000002,
  bearing: -76.68558217576117,
  roll: 0,
  elevation: 0.6365291232082922,
  padding: { top: 0, bottom: 0, left: 0, right: 0 },
};
// Mobil = samme som desktop foreløpig (ser om vi trenger noe annerledes).
export const INTRO6_MOBILE_START = {
  center: [10.508458346568318, 59.94135556817204],
  zoom: 14.295952287505694,
  pitch: 46.50000000000002,
  bearing: -76.68558217576117,
  roll: 0,
  elevation: 232.11231710912824,
  padding: { top: 0, bottom: 0, left: 0, right: 0 },
};
export const INTRO6_MOBILE_MID = {
  center: [10.661139623543733, 59.917330754145325],
  zoom: 11.57299128028978,
  pitch: 46.50000000000002,
  bearing: -76.68558217576117,
  roll: 0,
  elevation: 5.71661420509449,
  padding: { top: 0, bottom: 0, left: 0, right: 0 },
};
export const INTRO6_MOBILE_END = {
  center: [10.728421929846832, 59.909787210848066],
  zoom: 16.95157789830261,
  pitch: 46.50000000000002,
  bearing: -76.68558217576117,
  roll: 0,
  elevation: 0.6365291232082922,
  padding: { top: 0, bottom: 0, left: 0, right: 0 },
};

const INTRO6_MID_PROGRESS = 0.4;

function sampleIntro6(progress, start, midWp, end) {
  const t = clamp(progress);
  if (t <= 0) return { ...start, center: [...start.center], padding: { ...start.padding } };
  if (t >= 1) return { ...end, center: [...end.center], padding: { ...end.padding } };
  if (t < INTRO6_MID_PROGRESS) {
    return mixIntro5(start, midWp, smooth(t / INTRO6_MID_PROGRESS));
  }
  return mixIntro5(midWp, end, smooth((t - INTRO6_MID_PROGRESS) / (1 - INTRO6_MID_PROGRESS)));
}

export function sampleIntro6Camera(progress) {
  return sampleIntro6(progress, INTRO6_START, INTRO6_MID, INTRO6_END);
}

export function sampleIntro6CameraMobile(progress) {
  return sampleIntro6(progress, INTRO6_MOBILE_START, INTRO6_MOBILE_MID, INTRO6_MOBILE_END);
}

// --- Intro 7: samme kamerabane som intro 6, men bussen kjører langs ruta
// med grønn strek bak og markøren svevende over. Kamera gjenbrukes; det er
// buss-animasjonen (i main.js via onDraw) som er det nye.
export const INTRO7_START = INTRO6_START;
export const INTRO7_END = INTRO6_END;
export const INTRO7_MOBILE_START = INTRO6_MOBILE_START;
export const INTRO7_MOBILE_END = INTRO6_MOBILE_END;
export const sampleIntro7Camera = sampleIntro6Camera;
export const sampleIntro7CameraMobile = sampleIntro6CameraMobile;
